import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { PageHeader, StatusDot } from "@/components/admin-ui";

type Run = {
  id: number;
  source: string;
  mode?: string;
  status?: string;
  started_at?: string | null;
  finished_at?: string | null;
  stage?: string | null;
  progress?: number | null;
  rows_in?: number | null;
  rows_upserted?: number | null;
  error?: string | null;
};

// /admin/api/runs may answer {runs: Run[], next_page_token?} or a raw array.
type RunsResponse = { runs?: Run[] } | Run[];

export function RunsPage() {
  const [runs, setRuns] = useState<Run[]>([]);
  const [source, setSource] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRuns = async () => {
      setLoading(true);
      try {
        const q = source ? `?source=${encodeURIComponent(source)}` : "";
        const data = await api.get<RunsResponse>(`/admin/dataengine/runs${q}`);
        const arr: Run[] = Array.isArray(data) ? data : (data.runs ?? []);
        if (!cancelled) {
          setRuns(arr);
          setErr(null);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchRuns();
    return () => { cancelled = true; };
  }, [source]);

  // Source list is derived from observed rows so we don't hardcode it.
  const knownSources = Array.from(new Set(runs.map((r) => r.source))).sort();

  return (
    <>
      <PageHeader
        kicker="Data engine"
        title="Run history"
        sub="Every run the scheduler has executed. Filter by source to debug a sequence."
        actions={
          <select value={source} onChange={(e) => setSource(e.target.value)} style={{
            padding: "7px 10px", borderRadius: 7, border: "1px solid var(--rule)",
            background: "var(--surface)", color: "var(--ink-2)",
            fontFamily: "var(--font-ui)", fontSize: 12,
          }}>
            <option value="">All sources</option>
            {knownSources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        }
      />

      {err && (
        <div style={{ margin: "0 26px 14px", background: "rgba(122,45,31,0.08)", color: "var(--neg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
          {err}
        </div>
      )}

      <div style={{ margin: "0 26px", border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)", overflow: "hidden" }}>
        <div style={hdr}>
          <div>ID</div>
          <div>Source</div>
          <div>Mode</div>
          <div>Status</div>
          <div>Started</div>
          <div>Duration</div>
          <div style={{ textAlign: "right" }}>Rows in</div>
          <div style={{ textAlign: "right" }}>Rows upserted</div>
        </div>
        {loading && runs.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>Loading…</div>
        )}
        {runs.map((r) => (
          <div key={r.id} className="ops-row" style={{ ...row, cursor: "default" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>#{r.id}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 500, color: "var(--ink)" }}>{r.source}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{r.mode ?? "—"}</div>
            <div><StatusDot status={r.status ?? "—"} /></div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)" }}>{fmtTime(r.started_at)}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)" }}>{fmtDuration(r.started_at, r.finished_at)}</div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>{fmtInt(r.rows_in)}</div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>{fmtInt(r.rows_upserted)}</div>
          </div>
        ))}
        {!loading && runs.length === 0 && !err && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>No runs.</div>
        )}
      </div>
      <div style={{ height: 22 }} />
    </>
  );
}

function fmtTime(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}
function fmtDuration(start?: string | null, end?: string | null): string {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return "—";
  const sec = Math.round((e - s) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}
function fmtInt(n?: number | null) { return n == null ? "—" : n.toLocaleString("en-SG"); }

const hdr: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "60px 1fr 100px 100px 140px 80px 110px 130px",
  gap: 12, padding: "10px 14px", color: "var(--ink-4)",
  fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
  borderBottom: "1px solid var(--rule)", background: "var(--surface-2)",
};
const row: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "60px 1fr 100px 100px 140px 80px 110px 130px",
  gap: 12, padding: "10px 14px", alignItems: "center",
  borderBottom: "1px solid var(--rule)",
};
